import React from "react";
import clsx from "clsx";
import dayjs from "dayjs";

import Button from "components/Button";
import { InvoicingSVGIcon, NoInvoicingSVGIcon } from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { formatNumber, numberFormatter } from "helpers/formatNumber";
import { getClassByStatus } from "helpers/getClassByVariant";
import { useColorScheme } from "hooks/useColorScheme";
import { IFundingRequestItem } from "models/HaulPay";
import styles from "./style.module.scss";

interface IProps {
  data?: IFundingRequestItem;
  isSelected: boolean;
  handleClick: (id: string) => void;
  goToMoreDetails?: VoidFunction;
}

const BrokerFundingRequestItem: React.FC<IProps> = ({
  data,
  isSelected,
  handleClick,
  goToMoreDetails
}: Readonly<IProps>) => {
  const { currentIsDark } = useColorScheme();

  if (!data) {
    return (
      <div
        className={clsx(
          styles.ManagePayPendingItemContainer,
          !currentIsDark && "drop-shadow-[0_2px_4.5px_#9E9E9E33]"
        )}>
        <p className="text-center text-gray-500">No data available.</p>
      </div>
    );
  }

  return (
    <ul
      className={clsx(
        styles.ManagePayPendingItemContainer,
        !currentIsDark && "drop-shadow-[0_2px_4.5px_#9E9E9E33]"
      )}>
      <div className={styles.statusSection}>
        <div className="text-base font-medium">
          <span className="font-semibold">Status{" - "}</span>
          <span className={clsx("uppercase", getClassByStatus(data.status))}>{data.status}</span>
        </div>
        <input
          type="checkbox"
          checked={isSelected}
          className={clsx("peer", styles.selectedCheckbox)}
          onChange={() => handleClick(data.id)}
        />
      </div>
      <li>
        <span className="font-semibold">Carrier</span>
        <span>{data?.paymentProfile?.companyName || "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Debtor</span>
        <span>{data?.debtor?.name || "-"}</span>
      </li>
      <li>
        <span className="font-semibold">NOA Status</span>
        {/* <span>{data?.debtor?.creditApproved ?? "-"}</span> */}
      </li>
      <li>
        <span className="font-semibold">Invoice Amount</span>
        <div className="flex items-center gap-1">
          <span>{data ? formatNumber(data.amount, 2) : "-"}</span>
          {data?.contractType === "SELF_FINANCE_NO_INVOICE" ||
          data?.debtor?.name === "No Debtor" ? (
            <Tooltip message="Self-Finance No Invoice">
              <NoInvoicingSVGIcon className="size-6 text-approvedBorder" />
            </Tooltip>
          ) : data?.contractType === "SELF_FINANCE_WITH_INVOICE" ? (
            <Tooltip message="Self-Finance WIth Invoice">
              <InvoicingSVGIcon className="size-7 text-approvedBorder" />
            </Tooltip>
          ) : null}
        </div>
      </li>
      <li>
        <span className="font-semibold">Invoice #</span>
        <span>{data?.invoiceNumber || "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Invoice Sent</span>
        {data?.invoiceDate ? (
          <Tooltip
            message={
              <div className="flex flex-col gap-2">
                <span>Invoice Sent On:</span>
                <span>{dayjs(data.invoiceDate).format("MM/DD/YYYY hh:mm:ss A")}</span>
              </div>
            }
            wrapperClass="text-approvedBorder">
            <span className="text-approvedBorder">YES</span>
          </Tooltip>
        ) : (
          <span className="text-error">NO</span>
        )}
      </li>
      <li>
        <span className="font-semibold">Load #</span>
        <span>{data?.userLoadNumber || "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Requested</span>
        <span>{data ? dayjs(data.created).format("MM/DD/YYYY hh:mm A") : "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Debtor Rate</span>
        <span>{data?.debtor?.discountRate || "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Carrier %</span>
        <span>{data ? numberFormatter(Number(data.paymentProfileDiscountRate)) + "%" : "-"}</span>
      </li>
      <li>
        <span className="font-semibold">Carrier Pay</span>
        <span>{data ? formatNumber(Number(data.paymentProfileAmount), 2) : "-"}</span>
      </li>
      <li className="!items-start">
        <span className="font-semibold">Amnt. Funded.</span>
        <div className={styles.actionSection}>
          <span>{data ? formatNumber(data.amountFunded ?? 0, 2) : "-"}</span>
          <Button variant="outline" className={styles.moreButton} rounded onClick={goToMoreDetails}>
            More
          </Button>
        </div>
      </li>
    </ul>
  );
};

BrokerFundingRequestItem.displayName = "brokerFundingRequestItem";
export default BrokerFundingRequestItem;
